import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { Content, MainContent } from "../../../styles/styles";
import { SideBar } from "../../../components/nav/SideBar";
import {
  TableBody,
  TableCell,
  TableHead,
  ResponsiveTable,
  TableHeader,
  TableRow,
  ItemsPerPageContainer,
  StyledSelect,
  TableContainer,
  KoTitle1,
  EnHeadline,
  UseButton,
} from "../../../styles/sharedStyles";
import alternate_thumbnail from "../../../assets/document/alternate_thumbnail.webp";
import { convertTimestampToDate } from "../../../utils/formatTime";
import { useNavigationStore } from "store/navigationStore";
import HomeBackground from "components/background/HomeBackground";
import SearchInputComponent from "components/common/SearchInput";
import { Overview } from "components/dashboard/Overview";
import { designTokens, getTypography } from "styles/designTokens";
import { useListStore } from "store/listStore";
import { downloadZip } from "@/apis/project/download";
import {
  deleteListItem,
  getOutputDetail,
  ListItem,
} from "@/apis/dashboard/list";
import { truncateText } from "utils/truncateText";
import { FiArrowRight, FiDownload, FiMoreVertical } from "react-icons/fi";
import { useModal } from "store/modalStore"; // ✅ 전역 모달 사용
import { useNavigate } from "react-router-dom";
import { useProcessVuStore } from "@/store/processVuStore";
import { useProcessVhStore } from "@/store/processVhStore";

// ---------------------------------------------------------------------------
// Styled Components
// ---------------------------------------------------------------------------

const PaginationWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 20px 0;
  gap: 16px;
`;

const PaginationControls = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const PageInfo = styled.div`
  ${() => {
    try {
      return getTypography("korean", "body4");
    } catch {
      return `
        font-size: 14px;
        line-height: 20px;
      `;
    }
  }}
  color: #727272;
  margin: 0 16px;
`;

const PaginationButton = styled.button<{ disabled?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: 1px solid #EBEEF1;
  border-radius: 8px;
  background-color: ${(props) => (props.disabled ? "#F8F9FA" : "#FFFFFF")};
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    border-color: #30B0C7;
    background-color: #F8F9FA;
  }

  &:disabled {
    opacity: 0.5;
  }

  svg {
    width: 16px;
    height: 16px;
    stroke: ${(props) => (props.disabled ? "#C7CDD3" : "#727272")};
    transition: stroke 0.2s ease;
  }

  &:hover:not(:disabled) svg {
    stroke: #1A1D23;
  }
`;

const ChevronLeftIcon = () => (
  <svg viewBox="0 0 16 16" fill="none">
    <path
      d="M10 12L6 8L10 4"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ChevronRightIcon = () => (
  <svg viewBox="0 0 16 16" fill="none">
    <path
      d="M6 4L10 8L6 12"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// 옵션 드롭다운 컴포넌트
const OptionsDropdown = styled.div`
  position: relative;
  display: inline-block;
`;

const OptionsButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  background: transparent;
  border: none;

  svg {
    width: 18px;
    height: 18px;
    stroke: #727272;
    transition: stroke 0.2s ease;
  }

  &:hover svg {
    stroke: #1A1D23;
  }
`;

const DropdownMenu = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 4px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  border: 1px solid #EBEEF1;
  overflow: hidden;
  z-index: 1000;
  opacity: ${({ $isOpen }) => ($isOpen ? 1 : 0)};
  visibility: ${({ $isOpen }) => ($isOpen ? 'visible' : 'hidden')};
  transform: ${({ $isOpen }) => ($isOpen ? 'translateY(0)' : 'translateY(-8px)')};
  transition: all 0.2s ease;
  min-width: 90px;
`;

const DropdownItem = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 12px 16px;
  border: none;
  background: transparent;
  cursor: pointer;
  transition: background-color 0.2s ease;
  ${getTypography("korean", "body3")}
  color: #374151;

  &:hover {
    background-color: #F8F9FA;
  }

  &:first-child {
    border-bottom: 1px solid #EBEEF1;
  }

  svg {
    width: 16px;
    height: 16px;
    stroke: #727272;
  }

  &:hover svg {
    stroke: #1A1D23;
  }
`;

const DropdownItemDanger = styled(DropdownItem)`
  color: #DC2626;

  &:hover {
    background-color: #FEF2F2;
  }

  svg {
    stroke: #DC2626;
  }

  &:hover svg {
    stroke: #DC2626;
  }
`;

const RunningStatusBar = styled.div`
  width: 90px;
  height: 8px;
  border-radius: 2px;
  background-color: #eef0f8;
  overflow: hidden;
`;

const RunningStatusFill = styled.div<{ $percent: number }>`
  height: 100%;
  width: ${({ $percent }) =>
    `${Math.max(0, Math.min(100, Math.round($percent)))}%`};
  background-color: #5c6eff;
  transition: width 0.25s ease;
`;

const CheckboxContainer = styled.label`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  position: relative;
`;

const CheckboxInput = styled.input`
  display: none;
`;

const CheckboxButton = styled.div<{ $checked: boolean }>`
  width: 18px;
  height: 18px;
  border: 1px solid ${({ $checked }) => ($checked ? "#5C6EFF" : "#DEE2EB")};
  background-color: ${({ $checked }) => ($checked ? "#5C6EFF" : "white")};
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    border-color: #5C6EFF;
  }

  &::after {
    content: "";
    width: 12px;
    height: 9px;
    background-image: url("data:image/svg+xml,%3Csvg width='12' height='9' viewBox='0 0 12 9' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 4L4.5 7.5L11 1' stroke='white' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: center;
    visibility: ${({ $checked }) => ($checked ? "visible" : "hidden")};
  }
`;

const DeleteButton = styled.button<{ $disabled: boolean }>`
  padding: 8px 16px;
  background-color: ${({ $disabled }) => ($disabled ? "#F8F9FA" : "#DC2626")};
  color: ${({ $disabled }) => ($disabled ? "#C7CDD3" : "white")};
  border: none;
  border-radius: 8px;
  cursor: ${({ $disabled }) => ($disabled ? "not-allowed" : "pointer")};
  transition: all 0.2s ease;
  ${getTypography("korean", "body3")}
  font-weight: 500;

  &:hover:not(:disabled) {
    background-color: ${({ $disabled }) => ($disabled ? "#F8F9FA" : "#B91C1C")};
  }

  &:disabled {
    opacity: 0.6;
  }
`;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

type ProjectRouteId = "audion_vu" | "audion_vh";

const detectProjectRoute = (item: ListItem): ProjectRouteId | null => {
  const normalize = (value?: string | null) =>
    value?.toString().toLowerCase().trim();

  const componentMatch = item.components
    ?.map((component) => normalize(component))
    .find(
      (component): component is ProjectRouteId =>
        component === "audion_vu" || component === "audion_vh"
    );
  if (componentMatch) return componentMatch;

  const projectId = normalize(item.projectId);
  if (projectId === "audion_vu" || projectId === "audion_vh") {
    return projectId;
  }

  const projectName = normalize(item.projectName);
  if (projectName) {
    if (projectName.includes("highlight") || projectName.includes("하이라이트")) {
      return "audion_vh";
    }
    if (
      projectName.includes("understanding") ||
      projectName.includes("음성이해")
    ) {
      return "audion_vu";
    }
  }

  return null;
};

const ResultsPage: React.FC = () => {
  const [itemsPerPage, setItemsPerPage] = useState<number>(10);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const { isCollapsed } = useNavigationStore();

  const { openConfirm, openInfo } = useModal(); // ✅ 전역 모달 사용
  const { list, summary, page, fetchList, fetchSummary } = useListStore();
  const navigate = useNavigate();
  const setVuResult = useProcessVuStore((state) => state.setResult);
  const setVhResult = useProcessVhStore((state) => state.setResult);

  // -----------------------------------------------------------------------
  // 체크박스 선택 로직
  // -----------------------------------------------------------------------
  const handleSelectItem = (itemId: string) => {
    setSelectedItems((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    if (selectedItems.size === filteredList.length) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(filteredList.map((item) => item.id)));
    }
  };

  // 페이지 변경 또는 필터 변경 시 선택 초기화
  useEffect(() => {
    setSelectedItems(new Set());
  }, [currentPage, searchTerm, itemsPerPage]);

  // -----------------------------------------------------------------------
  // 삭제 로직 (전역 모달 기반)
  // -----------------------------------------------------------------------
  const handleDeleteItems = (itemIds: string[]) => {
    const count = itemIds.length;
    openConfirm({
      title: "항목 삭제",
      message: `정말로 선택된 ${count}개의 항목을 삭제하시겠습니까?`,
      confirmText: "삭제",
      cancelText: "취소",
      variant: "danger",
      onConfirm: async () => {
        try {
          await deleteListItem(itemIds);
          setSelectedItems(new Set());
          await fetchList({ page: currentPage, size: itemsPerPage });
          openInfo({
            title: "삭제 완료",
            message: `${count}개의 항목이 성공적으로 삭제되었습니다.`,
          });
        } catch (error) {
          console.error("Failed to delete items:", error);
          openInfo({
            title: "오류",
            message: "항목 삭제 중 오류가 발생했습니다.",
          });
        }
      },
    });
  };

  const handleDeleteItem = (itemId: string) => {
    handleDeleteItems([itemId]);
  };

  // -----------------------------------------------------------------------
  // 다운로드 로직
  // -----------------------------------------------------------------------
  const handleDownloadItem = async (itemId: string) => {
    try {
      const blob = await downloadZip(itemId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${itemId}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Failed to download file:", error);
      openInfo({
        title: "다운로드 실패",
        message: "파일 다운로드 중 문제가 발생했습니다.",
      });
    }
  };

  const handleViewResult = async (item: (typeof list)[number]) => {
    if (!item.documentId) {
      openInfo({
        title: "이동 불가",
        message: "문서 ID가 없어 결과 페이지로 이동할 수 없습니다.",
      });
      return;
    }

    const projectRoute = detectProjectRoute(item as ListItem);
    if (!projectRoute) {
      openInfo({
        title: "이동 불가",
        message: "지원되지 않는 프로젝트 유형입니다.",
      });
      return;
    }

    try {
      const response = await getOutputDetail(item.id);
      const result = response.content;

      if (projectRoute === "audion_vu") {
        setVuResult(item.documentId, result as any);
      } else if (projectRoute === "audion_vh") {
        setVhResult(item.documentId, result as any);
      }

      navigate(`/results/${projectRoute}/${item.documentId}`, {
        state: {
          projectName: item.projectName,
          result,
        },
      });
    } catch (error) {
      console.error("Failed to load output detail:", error);
      openInfo({
        title: "오류",
        message: "결과를 불러오는 중 문제가 발생했습니다.",
      });
    }
  };

  // -----------------------------------------------------------------------
  // 데이터 로딩
  // -----------------------------------------------------------------------
  useEffect(() => {
    fetchList({ page: currentPage, size: itemsPerPage });
    fetchSummary();
  }, []);

  // running 항목이 있을 때 폴링
  useEffect(() => {
    const isRunning = list.some((item) => item.status.startsWith("running"));
    if (!isRunning) return;

    const intervalId = setInterval(() => {
      fetchList({ page: currentPage, size: itemsPerPage });
    }, 5000);

    return () => clearInterval(intervalId);
  }, [list]);

  useEffect(() => {
    fetchList({ page: currentPage, size: itemsPerPage });
  }, [currentPage, itemsPerPage]);

  // -----------------------------------------------------------------------
  // UI 로직
  // -----------------------------------------------------------------------
  const handleItemsPerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleDropdownToggle = (itemId: string) => {
    setOpenDropdown(openDropdown === itemId ? null : itemId);
  };

  // 외부 클릭 시 드롭다운 닫기
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('[data-dropdown]')) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredList = list.filter(
    (item) =>
      item.projectName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.uploadedFileName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.components?.some((c) =>
        c.toLowerCase().includes(searchTerm.toLowerCase())
      )
  );

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------
  return (
    <HomeBackground showImage={false}>
      <Content $isCollapsed={isCollapsed}>
        <SideBar />
        <MainContent style={{ gap: "40px" }}>
          <EnHeadline>결과 관리</EnHeadline>
          <Overview summary={summary} />

          <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
            <KoTitle1>파일 목록</KoTitle1>
            <TableContainer>
              {/* 검색 영역 */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "36px",
                }}
              >
                <div style={{ width: "300px" }}>
                  <SearchInputComponent
                    placeholder="프로젝트명 또는 파일명으로 검색하세요."
                    value={searchTerm}
                    onChange={handleSearchChange}
                  />
                </div>
                <DeleteButton
                  $disabled={selectedItems.size === 0}
                  onClick={() => handleDeleteItems(Array.from(selectedItems))}
                  disabled={selectedItems.size === 0}
                >
                  선택 항목 삭제 ({selectedItems.size})
                </DeleteButton>
              </div>

              {/* 테이블 */}
              <ResponsiveTable>
                <TableHead>
                  <TableRow>
                    <TableHeader style={{ width: "50px", textAlign: "center" }}>
                      <CheckboxContainer>
                        <CheckboxInput
                          type="checkbox"
                          checked={
                            filteredList.length > 0 &&
                            selectedItems.size === filteredList.length
                          }
                          onChange={handleSelectAll}
                        />
                        <CheckboxButton
                          $checked={
                            filteredList.length > 0 &&
                            selectedItems.size === filteredList.length
                          }
                        />
                      </CheckboxContainer>
                    </TableHeader>
                    <TableHeader></TableHeader>
                    <TableHeader>파일명</TableHeader>
                    <TableHeader>프로젝트명</TableHeader>
                    <TableHeader>재생시간</TableHeader>
                    <TableHeader style={{ textAlign: "center" }}>상태</TableHeader>
                    <TableHeader style={{ textAlign: "center" }}>결과 보기</TableHeader>
                    <TableHeader>생성일자</TableHeader>
                    <TableHeader style={{ textAlign: "center" }}>옵션</TableHeader>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredList.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={8}
                        style={{
                          textAlign: "center",
                          padding: "100px 40px",
                          color: `${designTokens.colors.font.quaternary}`,
                        }}
                      >
                        파일이 없습니다.
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredList.map((item, index) => {
                      const isCompleted = item.status === "completed";
                      const isFailed = item.status === "failed";
                      const isRunning =
                        item.status === "running" ||
                        item.status.startsWith("running");

                      return (
                        <TableRow key={index}>
                          <TableCell style={{ textAlign: "center" }}>
                            <CheckboxContainer>
                              <CheckboxInput
                                type="checkbox"
                                checked={selectedItems.has(item.id)}
                                onChange={() => handleSelectItem(item.id)}
                              />
                              <CheckboxButton $checked={selectedItems.has(item.id)} />
                            </CheckboxContainer>
                          </TableCell>
                          <TableCell style={{ textAlign: "center" }}>
                            <img
                              src={
                                item.thumbnail
                                  ? item.thumbnail.startsWith("http")
                                    ? item.thumbnail
                                    : `${import.meta.env.VITE_DOWNLOAD_URL}/${item.thumbnail}`
                                  : alternate_thumbnail
                              }
                              alt="thumbnail"
                            />
                          </TableCell>
                          <TableCell>{truncateText(item.uploadedFileName, 15)}</TableCell>
                          <TableCell>{item.projectName}</TableCell>
                          <TableCell>{item.duration}</TableCell>
                          <TableCell>
                            {isFailed ? (
                              <div style={{ whiteSpace: "nowrap", textAlign: "center", color: "#FF0000" }}>실패</div>
                            ) : isRunning ? (
                              <div
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  width: "100%",
                                }}
                              >
                                <RunningStatusBar>
                                  <RunningStatusFill
                                    $percent={Number(
                                      item.status.replace(/\D+/g, "")
                                    )}
                                  />
                                </RunningStatusBar>
                              </div>
                            ) : (
                              <div style={{ whiteSpace: "nowrap", textAlign: "center" }}>{item.status === "completed" ? "완료" : ""}</div>
                            )}
                          </TableCell>
                          <TableCell style={{ textAlign: "center" }}>
                            {isCompleted ? (
                              <UseButton onClick={() => handleViewResult(item)}>
                              이동
                              <FiArrowRight />
                            </UseButton>
                            ) : (
                              <span style={{ color: "#C7CDD3" }}>-</span>
                            )}
                          </TableCell>
                          <TableCell style={{ whiteSpace: "nowrap" }}>
                            {convertTimestampToDate(Number(item.createdAt))}
                          </TableCell>
                          <TableCell style={{ textAlign: "center" }}>
                            <OptionsDropdown data-dropdown>
                              <OptionsButton onClick={() => handleDropdownToggle(item.id)}>
                                <FiMoreVertical />
                              </OptionsButton>
                              <DropdownMenu $isOpen={openDropdown === item.id}>
                                {isCompleted && (
                                  <DropdownItem onClick={() => handleDownloadItem(item.id)}>
                                    <FiDownload />
                                    저장
                                  </DropdownItem>
                                )}
                                <DropdownItemDanger onClick={() => handleDeleteItem(item.id)}>
                                  <svg viewBox="0 0 16 16" fill="none">
                                    <path
                                      d="M2 4h12M5.333 4V2.667a1.333 1.333 0 0 1 1.334-1.334h2.666a1.333 1.333 0 0 1 1.334 1.334V4M6.667 7.333v4M9.333 7.333v4M12 4v9.333a1.333 1.333 0 0 1-1.333 1.334H5.333a1.333 1.333 0 0 1-1.333-1.334V4h8Z"
                                      strokeWidth="1.5"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    />
                                  </svg>
                                  삭제
                                </DropdownItemDanger>
                              </DropdownMenu>
                            </OptionsDropdown>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </ResponsiveTable>

              {/* 페이지네이션 */}
              <PaginationWrapper>
                <ItemsPerPageContainer>
                  <label>페이지당 항목 수</label>
                  <StyledSelect value={page.size} onChange={handleItemsPerPageChange}>
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={30}>30</option>
                  </StyledSelect>
                </ItemsPerPageContainer>

                <PaginationControls>
                  <PaginationButton
                    disabled={!page.hasPrevious}
                    onClick={() => setCurrentPage(currentPage - 1)}
                  >
                    <ChevronLeftIcon />
                  </PaginationButton>

                  <PageInfo>
                    {page ? (
                      <>
                        페이지 {currentPage}
                        {page.totalPages > 0 && <span> / {page.totalPages}</span>}
                        {page.totalItems > 0 && <span> • 총 {page.totalItems}개</span>}
                      </>
                    ) : (
                      "페이지 정보 없음"
                    )}
                  </PageInfo>

                  <PaginationButton
                    disabled={!page.hasNext}
                    onClick={() => setCurrentPage(currentPage + 1)}
                  >
                    <ChevronRightIcon />
                  </PaginationButton>
                </PaginationControls>
              </PaginationWrapper>
            </TableContainer>
          </div>
        </MainContent>
      </Content>
    </HomeBackground>
  );
};

export default ResultsPage;
